/**
 * Team scheduling: who may serve where, and the auto-scheduler.
 *
 * Pure functions, so the server's answer and the client's preview are the
 * same computation, and the invariants are testable without a database:
 *
 *  - nobody is scheduled on a date they blocked out;
 *  - nobody is scheduled twice on one date (across plans, teams and positions
 *    — a vocalist at 9am is not also on cameras at 11am);
 *  - nobody serves more than their monthly limit;
 *  - nobody fills a position they are not qualified for.
 *
 * Fairness is rotation: among the eligible, the person who has served least
 * recently in the window goes first, then the one who has served least, then a
 * stable tie-break — so the same inputs always produce the same schedule.
 */

export interface SchedulingCandidate {
  personId: string
  name: string
  /** Positions this person is qualified for. */
  positionIds: string[]
  /** null ⇒ the team's default limit applies. */
  maxPerMonth: number | null
}

export interface Blockout {
  personId: string
  /** Inclusive local dates. */
  startsOn: string
  endsOn: string
}

export interface ExistingAssignment {
  personId: string
  date: string
  planId: string
  positionId: string
  /** Declined assignments do not count against anyone. */
  status: 'requested' | 'accepted' | 'declined'
}

export interface Slot {
  planId: string
  date: string
  /**
   * Every day the plan calls people in (a Thursday rehearsal for a Sunday
   * plan): being away on any of them is a blockout conflict. Defaults to `date`.
   */
  dates?: string[]
  positionId: string
  /** How many more people this position needs on this plan. */
  needed: number
}

export type ConflictKind = 'blocked_out' | 'double_booked' | 'over_limit' | 'not_qualified'

export interface Conflict {
  kind: ConflictKind
  message: string
}

export interface SchedulingContext {
  candidates: SchedulingCandidate[]
  blockouts: Blockout[]
  assignments: ExistingAssignment[]
  /** Applies when a candidate has no limit of their own. */
  defaultMaxPerMonth: number | null
}

const live = (assignment: ExistingAssignment) => assignment.status !== 'declined'
const monthOf = (date: string) => date.slice(0, 7)

export function isBlockedOut(personId: string, date: string, blockouts: Blockout[]): boolean {
  return blockouts.some((b) => b.personId === personId && b.startsOn <= date && date <= b.endsOn)
}

/** The first of these days the person is away, or null. */
export function blockedOutOn(
  personId: string,
  dates: readonly string[],
  blockouts: Blockout[],
): string | null {
  return [...dates].sort().find((date) => isBlockedOut(personId, date, blockouts)) ?? null
}

/**
 * Every reason this person should not take this position on this date —
 * empty when they can. The screen shows these as warnings on a manual
 * assignment; the auto-scheduler treats any of them as "not eligible".
 */
export function conflictsFor(
  candidate: SchedulingCandidate,
  slot: { planId: string; date: string; dates?: string[]; positionId: string },
  context: SchedulingContext,
): Conflict[] {
  const conflicts: Conflict[] = []
  if (!candidate.positionIds.includes(slot.positionId)) {
    conflicts.push({ kind: 'not_qualified', message: `${candidate.name} is not on this position.` })
  }
  const away = blockedOutOn(
    candidate.personId,
    slot.dates && slot.dates.length > 0 ? slot.dates : [slot.date],
    context.blockouts,
  )
  if (away) {
    conflicts.push({ kind: 'blocked_out', message: `${candidate.name} is away on ${away}.` })
  }
  const sameDay = context.assignments.filter(
    (a) => live(a) && a.personId === candidate.personId && a.date === slot.date,
  )
  if (sameDay.some((a) => !(a.planId === slot.planId && a.positionId === slot.positionId))) {
    conflicts.push({
      kind: 'double_booked',
      message: `${candidate.name} is already serving on ${slot.date}.`,
    })
  }
  const limit = candidate.maxPerMonth ?? context.defaultMaxPerMonth
  if (limit !== null) {
    const dates = new Set(
      context.assignments
        .filter(
          (a) =>
            live(a) && a.personId === candidate.personId && monthOf(a.date) === monthOf(slot.date),
        )
        .map((a) => a.date),
    )
    if (!dates.has(slot.date) && dates.size >= limit) {
      conflicts.push({
        kind: 'over_limit',
        message: `${candidate.name} already serves ${dates.size}× that month (limit ${limit}).`,
      })
    }
  }
  return conflicts
}

export interface AutoScheduleResult {
  assignments: ExistingAssignment[]
  unfilled: (Slot & { reason: string })[]
}

/**
 * Fills the slots it is given, in date order, never breaking a rule above.
 * Returns the proposed assignments (status `requested`) and what it could not
 * fill — the screen shows both, and a person reviews before anything is sent.
 */
export function autoSchedule(slots: Slot[], context: SchedulingContext): AutoScheduleResult {
  const working: ExistingAssignment[] = [...context.assignments]
  const proposed: ExistingAssignment[] = []
  const unfilled: AutoScheduleResult['unfilled'] = []

  const ordered = [...slots].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.planId.localeCompare(b.planId) ||
      a.positionId.localeCompare(b.positionId),
  )
  for (const slot of ordered) {
    for (let seat = 0; seat < slot.needed; seat++) {
      const ctx = { ...context, assignments: working }
      const eligible = context.candidates.filter(
        (candidate) =>
          conflictsFor(candidate, slot, ctx).length === 0 &&
          // A seat already held by them on this slot is not a new seat.
          !working.some(
            (a) =>
              live(a) &&
              a.personId === candidate.personId &&
              a.planId === slot.planId &&
              a.positionId === slot.positionId,
          ),
      )
      if (eligible.length === 0) {
        unfilled.push({
          ...slot,
          needed: slot.needed - seat,
          reason: 'Nobody qualified is free that day.',
        })
        break
      }
      const ranked = eligible
        .map((candidate) => {
          const served = working.filter((a) => live(a) && a.personId === candidate.personId)
          const last =
            served
              .filter((a) => a.date < slot.date)
              .map((a) => a.date)
              .sort()
              .at(-1) ?? ''
          return { candidate, last, count: served.length }
        })
        .sort(
          (a, b) =>
            a.last.localeCompare(b.last) ||
            a.count - b.count ||
            a.candidate.personId.localeCompare(b.candidate.personId),
        )
      const chosen = ranked[0]?.candidate
      if (!chosen) break
      const assignment: ExistingAssignment = {
        personId: chosen.personId,
        date: slot.date,
        planId: slot.planId,
        positionId: slot.positionId,
        status: 'requested',
      }
      working.push(assignment)
      proposed.push(assignment)
    }
  }
  return { assignments: proposed, unfilled }
}

// ---- running times ---------------------------------------------------------------

export interface TimedItem {
  id: string
  /** null ⇒ a header: no length, takes no time. */
  lengthSeconds: number | null
  /** Its phase: Before, During (the default) or After the service. */
  servicePosition?: string
}

/**
 * Each item's start, as seconds from the start of the service. Computed from
 * the order every time — never stored — so a reorder cannot leave a stale
 * start time behind.
 *
 * During items start at the service's start (0) and run on one after
 * another. Before items count down to it (the countdown ends as the service
 * begins), so their starts are negative; After items run on from the end of
 * the last During item. Each phase keeps its own clock, so the answer is the
 * same wherever its items sit in the list. Results come back in the order given.
 */
export function runningTimes(
  items: TimedItem[],
): { id: string; startsAt: number; endsAt: number }[] {
  const length = (item: TimedItem) => Math.max(0, item.lengthSeconds ?? 0)
  const lengths = orderLengths(items)
  const clocks = { pre: -lengths.before, during: 0, post: lengths.service }
  return items.map((item) => {
    const position = phaseOf(item)
    const startsAt = clocks[position]
    clocks[position] += length(item)
    return { id: item.id, startsAt, endsAt: clocks[position] }
  })
}

/** A timed item's phase; anything not Before or After is During. */
const phaseOf = (item: TimedItem): 'pre' | 'during' | 'post' =>
  item.servicePosition === 'pre' || item.servicePosition === 'post'
    ? item.servicePosition
    : 'during'

/**
 * An order's lengths by phase: the service's (During items only — its
 * length everywhere it is shown or printed), and what runs before and after
 * it, which do not count. Headers and items without a length add nothing.
 */
export function orderLengths(items: readonly TimedItem[]): {
  service: number
  before: number
  after: number
} {
  const out = { service: 0, before: 0, after: 0 }
  for (const item of items) {
    const seconds = Math.max(0, item.lengthSeconds ?? 0)
    const phase = phaseOf(item)
    if (phase === 'pre') out.before += seconds
    else if (phase === 'post') out.after += seconds
    else out.service += seconds
  }
  return out
}

/** How long the service itself runs: its During items, not what runs before or after. */
export function serviceSeconds(items: readonly TimedItem[]): number {
  return orderLengths(items).service
}

/** Moves the item at `from` to `to`, shifting the rest. */
export function reorder<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length)
    return [...items]
  const next = [...items]
  const [moved] = next.splice(from, 1)
  if (moved !== undefined) next.splice(to, 0, moved)
  return next
}

/** "4:05" from 245 seconds; "1:02:03" past an hour. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const rest = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${rest}` : `${m}:${rest}`
}

/** "4:05" or "245" → seconds; null if neither. */
export function parseDuration(text: string): number | null {
  const trimmed = text.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 60
  const match = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/.exec(trimmed)
  if (!match) return null
  return Number(match[1] ?? 0) * 3600 + Number(match[2]) * 60 + Number(match[3])
}

/** A team as a plan lists it: enough to tell whether someone is on it. */
export interface RosterTeam {
  id: string
  name: string
  positions: { members: { personId: string }[] }[]
}

/**
 * A plan's teams in the order one person reads them: the teams they are on
 * first, then the rest — each group alphabetical. Being asked to serve on
 * this plan for a team (`servingTeamIds`) counts as being on it. Nobody
 * signed in as a person (`personId` null): just alphabetical.
 */
export function teamsForViewer<T extends RosterTeam>(
  teams: readonly T[],
  personId: string | null,
  servingTeamIds: readonly string[] = [],
): { team: T; mine: boolean }[] {
  const byName = (a: { team: T }, b: { team: T }) =>
    a.team.name.localeCompare(b.team.name, undefined, { sensitivity: 'base' }) ||
    a.team.id.localeCompare(b.team.id)
  const marked = teams.map((team) => ({
    team,
    mine:
      personId !== null &&
      (servingTeamIds.includes(team.id) ||
        team.positions.some((p) => p.members.some((m) => m.personId === personId))),
  }))
  return [
    ...marked.filter((t) => t.mine).sort(byName),
    ...marked.filter((t) => !t.mine).sort(byName),
  ]
}
